import Datatable from "./datatable_controller";

// Connects to data-controller="event-datatable"
export default class extends Datatable {
  static targets = ['filter', 'statusFilter', 'statusChip', 'emptyMessage', 'emptyInstruction', 'emptyCta']

  static values = {
    status: {type: Boolean, default: false},
    initialStatus: String,
    defaultEmptyMessage: String,
    defaultEmptyInstruction: String,
    noResultsMessage: String,
    noResultsInstruction: String
  }

  connect() {
    // Seed the filter from ?status= (dashboard tiles / chip deep-links) BEFORE
    // the base class builds the DataTable, so the very first ajax load is
    // already filtered. "all" (or no value) means unfiltered.
    const initial = this.initialStatusValue
    this.selectedStatuses = initial && initial !== "all" ? [initial] : []
    // Reflect the seeded filter in the dropdown checkboxes too.
    if (this.hasStatusFilterTarget) {
      this.statusFilterTargets.forEach(checkbox => {
        checkbox.checked = this.selectedStatuses.includes(checkbox.value)
      })
    }
    super.connect()
  }

  get columns() {
    let table_columns = [
      {data: 'date'},
      {data: 'event'},
      {data: 'type'},
      {data: 'sold'},
      {data: 'revenue'},
      {data: 'status'},
      {data: 'actions'}
    ]
    return table_columns
  }

  // DataTables built-in search UI is hidden via CSS; we drive the search
  // API from a custom mobile input. Must be true so dataTable.search() works.
  get searching() {
    return true
  }

  // Override base runSearch to also swap the empty-state copy when a
  // search query is active (so "no events found" → "no events match…").
  runSearch(event) {
    const query = event.target.value
    clearTimeout(this._searchTimer)
    this._searchTimer = setTimeout(() => {
      if (this.dataTable) {
        this.updateEmptyState(query.trim().length > 0)
        this.dataTable.search(query).draw()
      }
    }, 250)
  }

  updateEmptyState(searching) {
    if (this.hasEmptyMessageTarget) {
      this.emptyMessageTarget.textContent = searching
        ? this.noResultsMessageValue
        : this.defaultEmptyMessageValue
    }
    if (this.hasEmptyInstructionTarget) {
      this.emptyInstructionTarget.textContent = searching
        ? this.noResultsInstructionValue
        : this.defaultEmptyInstructionValue
    }
    // "Create your first event" only makes sense in the TRUE empty state.
    // When a search returns nothing the org still has events, so hide it.
    if (this.hasEmptyCtaTarget) {
      this.emptyCtaTarget.classList.toggle("d-none", searching)
    }
  }

  get ordering() {
    return false
  }

  get lengthChange() {
    return false
  }

  manipulateData(data) {
    data = super.manipulateData(data)
    
    if (this.selectedStatuses && this.selectedStatuses.length > 0) {
      data.status_filters = this.selectedStatuses.join(',')
    }
    
    return data
  }

  // Quick single-status filter from the summary chips. Rides the exact same
  // selectedStatuses → status_filters mechanism as the dropdown filter, so the
  // chips and the dropdown can never disagree about what the table shows.
  filterByChip(event) {
    event.preventDefault()
    const status = event.params.status
    this.selectedStatuses = status === "all" ? [] : [status]

    // Mirror the chip choice into the multi-select dropdown checkboxes.
    // ("recurring" has no checkbox — every box simply unchecks.)
    if (this.hasStatusFilterTarget) {
      this.statusFilterTargets.forEach(checkbox => {
        checkbox.checked = this.selectedStatuses.includes(checkbox.value)
      })
    }

    this.highlightChip(status)
    this.updateEmptyState(this.selectedStatuses.length > 0)

    if (this.dataTable) {
      this.dataTable.ajax.reload()
    }
  }

  // Highlight the chip matching the current filter. When the dropdown selects
  // a combination no single chip represents, no chip is highlighted.
  highlightChip(status) {
    if (!this.hasStatusChipTarget) return
    this.statusChipTargets.forEach(chip => {
      chip.classList.toggle("active", chip.dataset.eventDatatableStatusParam === status)
    })
  }

  applyStatusFilter(event) {
    event.preventDefault()
    event.stopPropagation()

    this.selectedStatuses = []

    if (this.hasStatusFilterTarget) {
      this.statusFilterTargets.forEach(checkbox => {
        if (checkbox.checked) {
          this.selectedStatuses.push(checkbox.value)
        }
      })
    }

    // Keep the chips honest: highlight the single matching chip, "All" when
    // the dropdown cleared everything, or nothing for multi-status combos.
    this.highlightChip(this.selectedStatuses.length === 0 ? "all" :
      (this.selectedStatuses.length === 1 ? this.selectedStatuses[0] : null))

    // A status filter being active means the org has events but the filtered
    // set may be empty — treat that like the "no results" state, not first-run.
    this.updateEmptyState(this.selectedStatuses.length > 0)

    if (this.dataTable) {
      this.dataTable.ajax.reload()
    }

    // Close the dropdown by removing the 'show' class
    const dropdownMenu = event.target.closest('.dropdown-menu')
    const dropdownToggle = event.target.closest('.dropdown').querySelector('[data-bs-toggle="dropdown"]')

    if (dropdownMenu) {
      dropdownMenu.classList.remove('show')
    }
    if (dropdownToggle) {
      dropdownToggle.setAttribute('aria-expanded', 'false')
    }
  }

  removeStatusFilter(event) {
    event.preventDefault()
    event.stopPropagation()
    
    this.selectedStatuses = []

    if (this.hasStatusFilterTarget) {
      this.statusFilterTargets.forEach(checkbox => {
        checkbox.checked = false
      })
    }

    this.highlightChip("all")

    // Filter cleared — also account for any active search query when deciding
    // whether the empty state is "no results" vs. true first-run.
    const searchActive = this.hasSearchInputTarget && this.searchInputTarget.value.trim().length > 0
    this.updateEmptyState(searchActive)

    if (this.dataTable) {
      this.dataTable.ajax.reload()
    }

    // Close the dropdown by removing the 'show' class
    const dropdownMenu = event.target.closest('.dropdown-menu')
    const dropdownToggle = event.target.closest('.dropdown').querySelector('[data-bs-toggle="dropdown"]')

    if (dropdownMenu) {
      dropdownMenu.classList.remove('show')
    }
    if (dropdownToggle) {
      dropdownToggle.setAttribute('aria-expanded', 'false')
    }
  }
}
